import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Store, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth({ mode }) {
  const nav = useNavigate();
  const { applyAuth } = useAuth();
  const isRegister = mode === "register";
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", business_name: "", slug: "", gst_number: "", phone: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const routeByRole = (user) => {
    if (user.role === "super_admin") nav("/admin");
    else if (user.role === "vendor_admin") nav("/vendor");
    else nav("/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        const { data } = await api.post("/auth/register", {
          role: "vendor_admin", email: f.email, password: f.password, name: f.name,
          phone: f.phone, business_name: f.business_name, slug: f.slug, gst_number: f.gst_number,
        });
        applyAuth(data);
        toast.success("Store created! Awaiting platform approval.");
        nav("/vendor");
      } else {
        const { data } = await api.post("/auth/login", { email: f.email, password: f.password });
        applyAuth(data);
        toast.success(`Welcome back, ${data.user.name}`);
        routeByRole(data.user);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const quick = (email, password) => setF({ ...f, email, password });

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex">
      <div className="hidden lg:flex flex-col justify-between w-[42%] p-12 bg-gradient-to-b from-slate-900 to-[#0B0F17] border-r border-slate-800">
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl">
          <div className="h-8 w-8 rounded-lg bg-indigo-500 grid place-items-center"><Store className="h-5 w-5" /></div>Olhub
        </Link>
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight leading-tight">{isRegister ? "Launch your storefront in minutes." : "Manage your commerce empire."}</h2>
          <p className="text-slate-400 mt-4 leading-relaxed">Three role-based portals, one secure platform. Path-based multi-tenancy keeps every vendor's data fully isolated.</p>
        </div>
        <div className="text-slate-600 text-sm">© Olhub</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-8"><ArrowLeft className="h-4 w-4" /> Back home</Link>
          <h1 className="font-display text-3xl font-bold tracking-tight">{isRegister ? "Create your store" : "Portal sign in"}</h1>
          <p className="text-slate-400 mt-2">{isRegister ? "Vendor onboarding — set up your business." : "Super Admin & Vendor Admin access."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {isRegister && (
              <>
                <Field label="Your name" testid="reg-name"><Input value={f.name} onChange={set("name")} required data-testid="reg-name" className="bg-slate-900 border-slate-700 text-white" /></Field>
                <Field label="Business name"><Input value={f.business_name} onChange={set("business_name")} required data-testid="reg-business" className="bg-slate-900 border-slate-700 text-white" /></Field>
                <Field label="Store slug (platform.com/…)">
                  <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} required placeholder="my-store" data-testid="reg-slug" className="bg-slate-900 border-slate-700 text-white font-mono" />
                </Field>
                <Field label="GST number (optional)"><Input value={f.gst_number} onChange={set("gst_number")} data-testid="reg-gst" className="bg-slate-900 border-slate-700 text-white" /></Field>
              </>
            )}
            <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} required data-testid="auth-email" className="bg-slate-900 border-slate-700 text-white" /></Field>
            <Field label="Password"><Input type="password" value={f.password} onChange={set("password")} required data-testid="auth-password" className="bg-slate-900 border-slate-700 text-white" /></Field>
            <Button type="submit" disabled={loading} className="w-full bg-indigo-500 hover:bg-indigo-600 rounded-lg h-11" data-testid="auth-submit">
              {loading ? "Please wait…" : isRegister ? "Create store" : "Sign in"}
            </Button>
          </form>

          {!isRegister && (
            <div className="mt-6 p-4 rounded-lg bg-slate-900/60 border border-slate-800">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Demo accounts</div>
              <div className="space-y-2 text-sm">
                <button onClick={() => quick("superadmin@shopsphere.com", "admin123")} className="block w-full text-left text-slate-300 hover:text-indigo-400" data-testid="demo-superadmin">→ Super Admin</button>
                <button onClick={() => quick("owner@auraartisan.com", "vendor123")} className="block w-full text-left text-slate-300 hover:text-indigo-400" data-testid="demo-vendor">→ Vendor (Aura Artisan)</button>
              </div>
            </div>
          )}

          <p className="text-center text-slate-400 text-sm mt-6">
            {isRegister ? <>Already have an account? <Link to="/login" className="text-indigo-400 hover:underline">Sign in</Link></>
              : <>Want to sell? <Link to="/register" className="text-indigo-400 hover:underline">Open a store</Link></>}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-400 text-sm">{label}</Label>
      {children}
    </div>
  );
}
